import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { Alert } from '@ver-uds/uswds-react';
import EnrollmentStepperButtons from '../../../components/Enrollment/EnrollmentStepperButtons';
import HelpModal, { HelpModalProps } from '../../../components/HelpModal';
import EVerifyEmail from '../../../components/Enrollment/EVerifyEmail';
import LabelHint from '../../../components/Enrollment/CompanyInformation/LabelHint';

function initializeNode(): React.ReactNode {
  return <></>;
}

const EnrollmentChecklist = (): JSX.Element => {
  const [getModalContent, setModalContent] = useState({ header: '', body: initializeNode() });
  const [helpModalIsOpen, setHelpModalIsOpen] = useState(false);

  const openModal = (content: HelpModalProps['modalContent']) => {
    setModalContent(content);
    setHelpModalIsOpen(true);
  };
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      pageTitle: 'E-Verify - Enrollment Checklist',
    });
  }, []);
  return (
    <div className="margin-bottom-3 tablet:grid-col-9">
      <h1 className="margin-bottom-4">Enrollment Checklist</h1>
      <p>
        Deciding to enroll your company in E-Verify is the first step toward supporting a legal workforce. For more
        information, visit the{' '}
        <a href="https://www.e-verify.gov/employers/enrolling-in-e-verify" rel="noopener noreferrer" target="_blank">
          Enrolling in E-Verify
        </a>{' '}
        section of the E-Verify website or consult our{' '}
        <a
          href="https://www.e-verify.gov/quick-reference-guide-for-e-verify-enrollment"
          rel="noopener noreferrer"
          target="_blank"
        >
          Quick Reference Guide for E-Verify Enrollment
        </a>{' '}
        to learn how to enroll and start using E-Verify.
      </p>
      <Alert status="info" slim className="margin-y-3">
        <Alert.Heading>
          <p>Enrollment must be completed within a single web session</p>
        </Alert.Heading>
        <Alert.Text>
          Refer to this checklist to ensure you have the required information before you begin. Estimated time to
          complete is 10-15 minutes. Note you will be logged out of the system after 10 minutes of inactivity.
        </Alert.Text>
      </Alert>
      <h4>All Companies</h4>
      <ul>
        <li>Company’s legal name</li>
        <li>
          Employer identification number (also called a Federal Tax ID Number)
          <LabelHint openModal={openModal} label="Employer Identification Number (EIN)" className="margin-left-05" />
        </li>
        <li>Primary physical address which your company will access E-Verify</li>
        <li>Mailing address (if different from the physical address)</li>
        <li>
          Hiring sites that will participate in E-Verify in each state
          <LabelHint openModal={openModal} label="Hiring Sites" className="margin-left-05" />
        </li>
        <li>Total number of current employees</li>
        <li>
          Your company’s{' '}
          <a href="https://www.naics.com/search" rel="noopener noreferrer" target="_blank">
            North American Industry Classification System
          </a>{' '}
          (NAICS) code
          <LabelHint openModal={openModal} label="NAICS Code" className="margin-left-05" />
        </li>
        <li>Contact information (name, phone number, email address) for your company’s:</li>
        <li className="add-list-reset">
          <ul>
            <li>
              Your company’s Terms and Conditions and{' '}
              <a
                href="https://www.e-verify.gov/faq/what-is-the-memorandum-of-understanding-mou"
                rel="noopener noreferrer"
                target="_blank"
                aria-label="Learn more about the MOU in a new tab"
              >
                Memorandum of Understanding (MOU)
              </a>{' '}
              signatory
              <LabelHint
                openModal={openModal}
                label="Memorandum of Understanding (MOU) Signatory"
                className="margin-left-05"
              />
            </li>
            <li>
              Program Administrator(s)
              <LabelHint openModal={openModal} label="Program Administrator(s)" className="margin-left-05" />
            </li>
          </ul>
        </li>
      </ul>
      <h4>
        Federal Contractors Subjected to the FAR E-Verify Clause
        <LabelHint
          openModal={openModal}
          label="Federal Contractors Subjected to the FAR E-Verify Clause"
          className="margin-left-05"
        />
      </h4>
      <ul>
        <li>
          Unique Entity Identifier (UEI)
          <LabelHint openModal={openModal} label="Unique Entity Identifier (UEI)" className="margin-left-05" />
        </li>
      </ul>
      <p>
        Providing this information will help us to quickly verify your company. If you need additional guidance, email
        E-Verify support at <EVerifyEmail />.
      </p>
      <div />
      <EnrollmentStepperButtons nextButtonType="button" />
      <HelpModal
        modalContent={getModalContent}
        open={helpModalIsOpen}
        close={(): void => {
          setHelpModalIsOpen(false);
        }}
      />
    </div>
  );
};

export default EnrollmentChecklist;
